import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { SubmitHandler } from 'react-hook-form';

import {
    domainesSelector,
    GroupeTravailUtilisateurApiObject,
    useUtilisateurService,
    useUtilisateursServiceSelector,
    UtilisateursAppDispatch,
} from '@europrocurement/l2d-domain';

import { DomaineJsonldUtilisateurReadDomaineRead as Domains } from '@europrocurement/l2d-domain/openApi/ApiUtilisateur';

import { FlexyForm, FormObject, FormStructure } from '@europrocurement/flexy-form';
import { CenterCircularProgress } from '@europrocurement/flexy-components';
import { Box } from '@mui/material';
import {
    createWorkGroupFormStructure,
    SelectDomainsOptions,
} from '../../../functions/workgroupInfosFormStructure';
import { domainesOptionsSort } from '../../../functions/domainesOptionsSort';

/**
 * Types Declaration part.
 */
type ReturnedFormData = Pick<GroupeTravailUtilisateurApiObject, 'lib' | 'email' | 'phone'> & {
    domaine: SelectDomainsOptions;
    dCloture: Date | null;
    dCreation: Date;
};
type WorkGroupCreateInfosFormProps = {
    onSubmitCallback: SubmitHandler<ReturnedFormData>;
};

/**
 * Component Part
 */
export const WorkGroupCreateInfosForm: React.FunctionComponent<WorkGroupCreateInfosFormProps> =
    function ({ onSubmitCallback }) {
        /**
         * Domaines part.
         */
        const dispatch = useDispatch<UtilisateursAppDispatch>();
        const { getDomaines } = useUtilisateurService();
        const domainesDataSource = useUtilisateursServiceSelector(domainesSelector);
        const domainesLoaded = domainesDataSource.status === 'succeeded';
        const [domaines, setDomaines] = useState<Domains[]>([]);
        const [domainesReady, setDomainesReady] = useState(false);
        const [domainesOptionsList, setDomainesOptionsList] = useState<SelectDomainsOptions[]>([]);
        const [domainesOptionsReady, setDomainesOptionsReady] = useState<boolean>(false);

        // On Mount: get domaines
        useEffect(() => {
            const fetchDomains = () => {
                // No pagination to get all items
                dispatch({
                    type: `${domainesDataSource.slicename}/set${domainesDataSource.name}Pagination`,
                    payload: { page: 0, itemsPerPage: 20 },
                });
                dispatch(getDomaines({}));
            };
            fetchDomains();
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, []);

        // Set domaines data once loaded
        useEffect(() => {
            if (domainesLoaded) {
                setDomaines(domainesDataSource.data);
                setDomainesReady(true);
            }
        }, [domainesLoaded, domainesDataSource.data]);

        useEffect(() => {
            setDomainesOptionsReady(false);
            if (domainesReady === true) {
                // Make domaines full list an array of valid select options for the form.
                const options = domainesOptionsSort(domaines);
                setDomainesOptionsList(options);
                setDomainesOptionsReady(true);
            }
        }, [domainesReady, domaines]);

        /**
         * Form structure part.
         */
        // Set default values and options
        const defaultValues: FormObject = {
            lib: null,
            email: null,
            domaine: domainesOptionsReady
                ? { label: domainesOptionsList[0].label, value: domainesOptionsList[0].value }
                : null,
            phone: null,
            dCreation: new Date(),
            dCloture: null,
        };

        const formStructure: FormStructure[] = createWorkGroupFormStructure(domainesOptionsList);

        return (
            <Box>
                {!domainesReady || !domainesOptionsReady ? (
                    <CenterCircularProgress sx={{ width: '50vw' }} />
                ) : (
                    <FlexyForm
                        formObject={defaultValues}
                        formStructure={formStructure}
                        onSubmit={onSubmitCallback}
                        submitButton={{ label: 'Créer le groupe', displayed: true }}
                    />
                )}
            </Box>
        );
    };

export default WorkGroupCreateInfosForm;
